import moment from 'moment';
import React from 'react';

import {
  FaBootstrap,
  FaGithubAlt,
  FaGitlab,
  FaNode,
  FaReact,
  FaVuejs,
  FaBalanceScaleRight,
  FaGraduationCap,
  FaFont,
  FaCss3Alt
} from 'react-icons/fa';
import { SiNestjs, SiConfluence, SiPostgresql } from 'react-icons/si';
import { HiChatAlt2 } from 'react-icons/hi';
import { DiScrum, DiJira } from 'react-icons/di';
import { GiArcheryTarget, GiOpenBook, GiCompass } from 'react-icons/gi';
import { FaHandsHoldingChild, FaBinoculars } from 'react-icons/fa6';
import { LuBrainCircuit } from 'react-icons/lu';
import { TbClockDollar, TbBrandCSharp } from 'react-icons/tb';
import { CgWebsite } from 'react-icons/cg';

import {
  SiJavascript,
  SiJquery,
  SiMongodb,
  SiMysql,
  SiNpm,
} from 'react-icons/si';
import { VscVscode } from 'react-icons/vsc';

const getExperienceDuration = () => {
  return `${getExperienceDurationNumber()}+ años de experiencia`;
};

const getExperienceDurationNumber = () => {
  const starts = moment('2008-08-16');
  const ends = moment();
  const duration = moment.duration(ends.diff(starts));
  const years = Math.floor(duration.asYears());
  return years;
};

const defaultThemeColor = {
  hsl: {
    h: 0,
    s: 1,
    l: 0.4,
    a: 1,
  },
  hex: '#cc0000',
  rgb: {
    r: 204,
    g: 0,
    b: 0,
    a: 1,
  },
  hsv: {
    h: 0,
    s: 1,
    v: 0.8,
    a: 1,
  },
  oldHue: 352.71844660194176,
  source: 'hex',
};

const about = {
  details: (
    <React.Fragment>
      <p>
        Ingeniero Full-Stack Senior con más de {getExperienceDurationNumber()} años de experiencia en la construcción de sistemas distribuidos a gran escala. Experto en Node.js, NestJS y .NET. Líder probado en la optimización de flujos de trabajo de desarrollo, mentoría de equipos de alto rendimiento y dirección de decisiones arquitectónicas que equilibran la excelencia técnica con la escalabilidad del negocio.
      </p>

      <ul>
        <li>✓ Mentoría y desarrollo de talento</li>
        <li>✓ Automatización y optimización de procesos</li>
        <li>✓ Arquitectura backend (Node.js, NestJS)</li>
        <li>✓ Desarrollo frontend (React, Angular)</li>
        <li>✓ Ecosistema .NET (C#, VB)</li>
        <li>✓ Bases de datos: PostgreSQL, MySQL, MongoDB</li>
        <li>✓ Cloud, CI/CD y sistemas distribuidos</li>
      </ul>

      <p>
        A lo largo de mi carrera, he diseñado e implementado sistemas escalables, procesos de automatización y aplicaciones críticas para el negocio en múltiples industrias. Disfruto resolver problemas complejos y transformarlos en soluciones simples, eficientes y fáciles de mantener.
      </p>

      <p>
        Me apasiona construir software confiable, mejorar procesos de desarrollo y ayudar a los equipos a entregar productos de alta calidad.
      </p>
    </React.Fragment>
  ),
  languages: [
    { name: 'English', level: 'B2+' },
    { name: 'Spanish', level: 'Native' },
  ],
  taglines: [
    'Desarrollador full stack',
    'Desarrollador web',
    'Arquitectura de software',
    'Freelance',
  ],
};

const skills = [
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Communicación',
    iconColor: '#7B68EE',
    icon: <HiChatAlt2 />,
    skillQualification: 10,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Liderazgo',
    iconColor: '#FF5733',
    icon: <GiCompass />,
    skillQualification: 5,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Creación de equipos',
    iconColor: '#1E90FF',
    icon: <FaHandsHoldingChild />,
    skillQualification: 7,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Resilencia',
    iconColor: '#6A5ACD',
    icon: <LuBrainCircuit />,
    skillQualification: 9,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Resolución de problemas',
    iconColor: '#FFD700',
    icon: <GiArcheryTarget />,
    skillQualification: 9,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Etica Laboral',
    iconColor: '#8B4513',
    icon: <FaBalanceScaleRight />,
    skillQualification: 10,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Transparencia',
    iconColor: '#00CED1',
    icon: <GiOpenBook />,
    skillQualification: 10,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Control del tiempo',
    iconColor: '#FF4500',
    icon: <TbClockDollar />,
    skillQualification: 8,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Curiosidad',
    iconColor: '#7B68EE',
    icon: <FaBinoculars />,
    skillQualification: 10,
  },
  {
    type: 'soft',
    subtype: 'soft',
    title: 'Aprendizaje continuo',
    iconColor: '#4682B4',
    icon: <FaGraduationCap />,
    skillQualification: 7,
  },
  {
    type: 'hard',
    subtype: 'tool-platform',
    title: 'SCRUM',
    iconColor: '#007ACC',
    icon: <DiScrum />,
    skillQualification: 7,
  },
  {
    type: 'hard',
    subtype: 'tool-platform',
    title: 'Jira',
    iconColor: '#0052CC',
    icon: <DiJira />,
    skillQualification: 7,
  },
  {
    type: 'hard',
    subtype: 'tool-platform',
    title: ' Confluence',
    iconColor: '#172B4D',
    icon: <SiConfluence />,
    skillQualification: 8,
  },

  {
    type: 'hard',
    subtype: 'programming',
    title: 'NestJS',
    iconColor: '#E0234E',
    icon: <SiNestjs />,
    skillQualification: 7,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'JavaScript',
    iconColor: '#E1CC1B',
    icon: <SiJavascript />,
    skillQualification: 8,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'React.js',
    iconColor: '#31B2FB',
    icon: <FaReact />,
    skillQualification: 6,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'Vue.js',
    iconColor: '#42B983',
    icon: <FaVuejs />,
    skillQualification: 5,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'Node.js',
    iconColor: '#5C9553',
    icon: <FaNode />,
    skillQualification: 8,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'jQuery',
    iconColor: '#0769AD',
    icon: <SiJquery />,
    skillQualification: 7,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: '.net framework',
    iconColor: '#0769AD',
    icon: <TbBrandCSharp />,
    skillQualification: 5,
  },
  {
    type: 'hard',
    subtype: 'database',
    title: 'PostgreSQL',
    iconColor: '#4479A1',
    icon: <SiPostgresql />,
    skillQualification: 4,
  },
  {
    type: 'hard',
    subtype: 'database',
    title: 'MySQL',
    iconColor: '#4479A1',
    icon: <SiMysql />,
    skillQualification: 4,
  },
  {
    type: 'hard',
    subtype: 'database',
    title: 'MongoDB',
    iconColor: '#4479A1',
    icon: <SiMongodb />,
    skillQualification: 7,
  },
  {
    type: 'hard',
    subtype: 'tool-platform',
    title: 'GitHub',
    iconColor: '#F44D27',
    icon: <FaGithubAlt />,
    skillQualification: 8,
  },
  {
    type: 'hard',
    subtype: 'tool-platform',
    title: 'GitLab',
    iconColor: '#F44D27',
    icon: <FaGitlab />,
    skillQualification: 8,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'CSS',
    iconColor: '#2862E9',
    icon: <FaCss3Alt />,
    skillQualification: 6,
  },
  {
    type: 'hard',
    subtype: 'programming',
    title: 'Bootstrap',
    iconColor: '#533B78',
    icon: <FaBootstrap />,
    skillQualification: 6,
  },
];
const projects = [
  {
    title: 'Expenses NestJS Micro service',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/nestjs.png',
    images: [process.env.PUBLIC_URL + '/assets/img/nestjs.png'],
    icon: <SiNestjs />,
    skills: [
      {
        title: 'NestJS',
        color: '#f1a900',
      },
      {
        title: 'Postgre',
        color: '#e66699',
      },
      {
        title: 'Api Rest',
        color: '#17572d',
      },
      {
        title: 'CI/CD',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Package'],
    description: (
      <React.Fragment>
        Microservicio RESTful para la gestión de gastos, desarrollado con NestJS, TypeORM y PostgreSQL. Este servicio ofrece operaciones CRUD para registros de gastos con funciones de filtrado, paginación y gestión de estado.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/expenses-njs',
      },
    ],
  },
  {
    title: 'Easy Portforward',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/npm.jpeg',
    images: [
      process.env.PUBLIC_URL +
      '/assets/img/projects/easy-portforward/screenshot.png',
    ],
    icon: <SiNpm />,
    skills: [
      {
        title: 'express, websocket',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Librería'],
    description: (
      <React.Fragment>
        Esta es un utilitario instalado a través de npm que te permite gestionar
        y conectar servicios de Kubernetes utilizando el reenvío de puertos de
        manera sencilla.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/easy-portforward',
      },
      {
        title: 'NPM',
        url: 'https://www.npmjs.com/package/easy-portforward',
      },
    ],
  },
  {
    title: 'Techoryze platform',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/projects/techoryze/sm.png',
    icon: <CgWebsite />,
    images: [
      process.env.PUBLIC_URL + '/assets/img/projects/techoryze/sc1.png',
      process.env.PUBLIC_URL + '/assets/img/projects/techoryze/sc2.png',
      process.env.PUBLIC_URL + '/assets/img/projects/techoryze/sc3.png',
    ],
    skills: [
      {
        title: 'Typescript.js',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Página web'],
    description: (
      <React.Fragment>
        <p>
          Techoryze ofrece el soporte de aprendizaje en tiempo real necesario
          para que los educadores aceleren el uso de la tecnología en el aula.
        </p>
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'http://www.techoryze.com',
      },
    ],
  },
  {
    title: 'Easy CSV 2 JSON',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/npm.jpeg',
    images: [process.env.PUBLIC_URL + '/assets/img/npm.jpeg'],
    icon: <SiNpm />,
    skills: [
      {
        title: 'Typescript.js',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Librería'],
    description: (
      <React.Fragment>
        Convierte de un archivo CSV simple a JSON. Con esta poderosa herramienta
        podrás procesar archivos CSV y hacer cálculos en segundos.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/easy-csv2json',
      },
      {
        title: 'NPM',
        url: 'https://www.npmjs.com/package/easy-csv2json',
      },
    ],
  },
  {
    title: 'Easy JSON Diff',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/npm.jpeg',
    images: [process.env.PUBLIC_URL + '/assets/img/npm.jpeg'],
    icon: <SiNpm />,
    skills: [
      {
        title: 'Typescript.js',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Librería'],
    description: (
      <React.Fragment>
        Paquete que permite obtener la diferencia entre dos objetos JSON
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/easy-json-diff',
      },
    ],
  },
  {
    title: 'Easy postman 2 markdown',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/npm.jpeg',
    images: [process.env.PUBLIC_URL + '/assets/img/npm.jpeg'],
    icon: <SiNpm />,
    skills: [
      {
        title: 'Typescript.js',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Librería'],
    description: (
      <React.Fragment>
        La manera más fácil de generar un documento markdown a partir de la
        documentación de Postman. Con esta herramienta también puedes aplicar
        estilo y personalización a tus documentos.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/easy-postman2md',
      },
      {
        title: 'NPM',
        url: 'https://www.npmjs.com/package/easy-postman2md',
      },
    ],
  },
  {
    title: 'Crypto animal font',
    thumbnail:
      'https://github.com/ariassd/crypto-animal-font/raw/master/assets/ancient-egypt-banner.png',
    images: [
      'https://github.com/ariassd/crypto-animal-font/raw/master/assets/ancient-egypt-banner.png',
    ],
    icon: <FaFont />,
    skills: [
      {
        title: 'FontLab7',
        color: '#31B2FB',
      },
    ],
    categories: ['Otros'],
    description: (
      <React.Fragment>
        Esta fuente convierte caracteres alfabéticos en símbolos. Fácil de usar
        y divertida para niños y proyectos escolares. La versión ordenada en
        inglés utiliza animales cuyo nombre comienza con la misma letra.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/crypto-animal-font',
      },
    ],
  },
  {
    title: 'Snack Query Builder NPM',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/npm.jpeg',
    images: [process.env.PUBLIC_URL + '/assets/img/npm.jpeg'],
    icon: <SiNpm />,
    skills: [
      {
        title: 'Typescript.js',
        color: '#31B2FB',
      },
      {
        title: 'Node.js',
        color: '#5C9553',
      },
    ],
    categories: ['Librería'],
    description: (
      <React.Fragment>
        Snack Query Builder NPM es una herramienta útil para construir consultas
        SQL directamente desde TypeScript utilizando una sintaxis clara y fácil
        de entender.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/snack-query-builder',
      },
      {
        title: 'NPM',
        url: 'https://www.npmjs.com/package/snack-query-builder',
      },
    ],
  },
  {
    title: 'NestJS Date format interceptor',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/npm.jpeg',
    images: [process.env.PUBLIC_URL + '/assets/img/npm.jpeg'],
    icon: <SiNestjs />,
    skills: [
      {
        title: 'Typescript.js',
        color: '#31B2FB',
      },
      {
        title: 'Nest.js',
        color: '#5C9553',
      },
    ],
    categories: ['Librería'],
    description: (
      <React.Fragment>
        Este complemento te permite interceptar fechas y formatearlas de
        múltiples maneras.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/nestjs-date-format-interceptor',
      },
      {
        title: 'NPM',
        url: 'https://www.npmjs.com/package/nestjs-date-format-interceptor',
      },
    ],
  },
  {
    title: 'Numbers To Words Spanish',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/csharp.png',
    images: [process.env.PUBLIC_URL + '/assets/img/csharp.png'],
    icon: <TbBrandCSharp />,
    skills: [
      {
        title: 'C#',
        color: '#31B2FB',
      },
      {
        title: '.NET',
        color: '#5C9553',
      },
    ],
    categories: ['.NET'],
    description: (
      <React.Fragment>
        Spanish <br />
        Esta es una extensión de la clase Decimal para convertir números a
        texto, útil para bancos y aplicaciones financieras.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/NumberToWords',
      },
    ],
  },
  {
    title: 'Address book',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/csharp.png',
    images: [process.env.PUBLIC_URL + '/assets/img/csharp.png'],
    icon: <TbBrandCSharp />,
    skills: [
      {
        title: 'C#',
        color: '#31B2FB',
      },
      {
        title: '.NET',
        color: '#5C9553',
      },
    ],
    categories: ['.NET'],
    description: (
      <React.Fragment>
        Agenda de direcciones simple realizada con Visual Studio para Mac
        (Community) y MongoDB. Este proyecto no tiene ningún tipo de seguridad
        en esta primera versión.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/AddressBook',
      },
    ],
  },
  {
    title: 'ScreenDraw 1.0 For Windows',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/csharp.png',
    images: [process.env.PUBLIC_URL + '/assets/img/csharp.png'],
    icon: <TbBrandCSharp />,
    skills: [
      {
        title: 'C#',
        color: '#31B2FB',
      },
      {
        title: '.NET',
        color: '#5C9553',
      },
    ],
    categories: ['.NET'],
    description: (
      <React.Fragment>
        Aplicación de dibujo en pantalla simple. Dibuja directamente en la
        pantalla, realiza una captura de pantalla y guarda los resultados en la
        carpeta de escritorio.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/ScreenDraw',
      },
    ],
  },
  {
    title: 'Terminal loader',
    thumbnail: process.env.PUBLIC_URL + '/assets/img/vscode.png',
    images: [process.env.PUBLIC_URL + '/assets/img/vscode.png'],
    icon: <VscVscode />,
    skills: [
      {
        title: 'typescript',
        color: '#31B2FB',
      },
      {
        title: 'VSCode',
        color: '#5C9553',
      },
    ],
    categories: ['VS Code'],
    description: (
      <React.Fragment>
        Esta extensión permite abrir múltiples terminales apiladas lado a lado y
        ejecutar un comando diferente en cada una. Perfecto para trabajar en un
        espacio de trabajo con múltiples proyectos.
      </React.Fragment>
    ),
    links: [
      {
        title: 'Homepage',
        url: 'https://github.com/ariassd/vscode-load-terminals',
      },
      {
        title: 'Visual Studio',
        url: 'https://marketplace.visualstudio.com/items?itemName=ariassd.terminal-loader',
      },
    ],
  },
];

const experiences = [
  // {
  //   company: 'Freelance',
  //   from: '2026',
  //   to: 'presente',
  //   position: 'Ingeniero Full-Stack / Especialista Backend',
  //   responsibilities: [
  //     'Diseñé y optimicé plataformas de alta disponibilidad, garantizando un tiempo de actividad (uptime) del 99.9% e integridad de datos en entornos de producción críticos.',
  //     'Lideré la gestión de arquitecturas complejas de microservicios, implementando flujos de trabajo de recuperación automatizados para minimizar el tiempo de resolución de incidentes.',
  //     'Arquitecté soluciones de automatización de procesos de extremo a extremo, reduciendo la carga operativa manual en un 70% y acelerando los ciclos de entrega de funcionalidades.',
  //     'Diseñé y estructuré arquitecturas de datos escalables capaces de manejar tráfico de alto volumen con una recuperación de baja latencia.',
  //     'Lideré iniciativas de ingeniería de rendimiento de bases de datos, optimizando la ejecución de consultas complejas y esquemas de almacenamiento para mejorar el rendimiento del sistema.',
  //   ],
  //   techStack: [
  //     '✓ Full-stack: Python, React, Angular, WordPress',
  //     '✓ Arquitectura API: Servicios RESTful, Microservicios',
  //     '✓ Bases de datos: PostgreSQL, MongoDB, MySQL, SQLServer, Redis',
  //     '✓ Backend: Node.js, NestJS, RabbitMQ, TypeScript, C#, .NET, Python, PHP'
  //   ],
  // },
  {
    company: 'Gamanza Costa Rica',
    from: '2020',
    to: '2026',
    position: 'Desarrollador Backend',
    responsibilities: [
      'Lideré e instruí equipos de ingeniería en la gestión de sistemas CRM y sistemas de alta carga de datos, impulsando la toma de decisiones para arquitecturas operativas complejas.',
      'Arquitecté estructuras de información internas para garantizar la escalabilidad y fiabilidad de los sistemas a largo plazo.',
      'Gestioné sistemas de datos a gran escala, asegurando una precisión del 100% en el procesamiento de registros de alto volumen.',
      'Automatice flujos de trabajo administrativos y procesos de entrada de datos, aumentando significativamente la eficiencia operativa.',
      'Entregué informes operativos basados en datos para respaldar la toma de decisiones estratégicas a nivel ejecutivo.',
      'Implementé integraciones de herramientas automatizadas para reducir las tareas manuales repetitivas en un 80%.',
    ],
    techStack: [
      '✓ Sistemas Principales: Plataformas de Gamificación y CRM',
      '✓ Gestión de Bases de Datos: MongoDB, PostgreSQL, Redis',
      '✓ Backend: TypeScript, Node.js, NestJS, RabbitMQ, Docker, CI/CD',
    ],
  },
  // {
  //   company: 'Gamanza Costa Rica',
  //   from: '2021',
  //   to: '2026',
  //   position: 'Desarrollador Backend | Tech Lead',
  //   responsibilities: [
  //     'Lideré e instruí equipos de ingeniería en la gestión de sistemas CRM y sistemas de alta carga de datos, impulsando la toma de decisiones para arquitecturas operativas complejas.',
  //     'Arquitecté estructuras de información internas para garantizar la escalabilidad y fiabilidad de los sistemas a largo plazo.',
  //     'Gestioné sistemas de datos a gran escala, asegurando una precisión del 100% en el procesamiento de registros de alto volumen.',
  //     'Automatice flujos de trabajo administrativos y procesos de entrada de datos, aumentando significativamente la eficiencia operativa.',
  //     'Entregué informes operativos basados en datos para respaldar la toma de decisiones estratégicas a nivel ejecutivo.'
  //   ],
  //   techStack: [
  //     '✓ Sistemas Principales: Plataformas de Gamificación y CRM',
  //     '✓ Gestión de Bases de Datos: MongoDB, PostgreSQL, Redis',
  //     '✓ Backend: TypeScript, Node.js, NestJS, RabbitMQ, Docker, CI/CD',
  //   ],
  // },
  // {
  //   company: 'Gamanza Costa Rica',
  //   from: '2020',
  //   to: '2021',
  //   position: 'Desarrollador Backend Senior',
  //   responsibilities: [
  //     'Desarrollé módulos principales de CRM, garantizando una alta disponibilidad del sistema y precisión de datos en tiempo real para los usuarios finales.',
  //     'Resolví cuellos de botella operativos complejos mediante la optimización de flujos de trabajo en entornos digitales de alto tráfico.',
  //     'Implementé integraciones de herramientas automatizadas para reducir las tareas manuales repetitivas en un 80%.',
  //     'Diseñé estructuras de datos eficientes, permitiendo la recuperación rápida y el procesamiento de alta velocidad de grandes conjuntos de datos.',
  //     'Optimicé estrategias de almacenamiento e indexación en bases de datos para mantener el rendimiento del sistema a medida que escalaba el volumen de transacciones.',
  //   ],
  //   techStack: [
  //     '✓ Sistema Principal: Gamanza CRM',
  //     '✓ Gestión de Bases de Datos: MongoDB, PostgreSQL, Redis',
  //     '✓ Backend: TypeScript, Node.js, NestJS, RabbitMQ, Docker, CI/CD',
  //   ],
  // },
  {
    company: 'Techoryze',
    from: '2021',
    to: '2021',
    position: 'Desarrollador Full Stack (Freelance)',
    responsibilities: [
      'Diseñé e implementé la plataforma de help-desk Techoryze desde la concepción hasta el despliegue.',
      'Arquitecté estructuras de información para soportar la recuperación de datos de alto volumen para la generación de informes de usuario en tiempo real.',
      'Garantice la fiabilidad y disponibilidad de la plataforma mediante un monitoreo y optimización rigurosos del rendimiento.',
    ],
    techStack: [
      '✓ Sistema Principal: Techoryze Help Desk',
      '✓ Gestión de Bases de Datos: MongoDB, PostgreSQL',
      '✓ Backend: TypeScript, Node.js, NestJS, Docker, CI/CD',
    ],
  },
  {
    company: 'Creative Drive',
    from: '2018',
    to: '2020',
    position: 'Desarrollador Backend Senior .NET',
    responsibilities: [
      'Modernicé las plataformas gubernamentales de PROCOMER mediante la auditoría y optimización de los sistemas de registros institucionales.',
      'Garanticé la consistencia de los datos en entornos de múltiples bases de datos relacionales utilizando arquitectura SQL Server.',
      'Mejoré las velocidades de recuperación de información mediante la reingeniería de la organización de archivos digitales y patrones de acceso.',
    ],
    techStack: [
      '✓ Sistema Principal: Sistema VUE (PROCOMER)',
      '✓ Gestión de Bases de Datos: SQL Server',
      '✓ Backend: C#, AppFabric, Microservicios, XML, Angular, jQuery, MVC.NET',
    ],
  },
  {
    company: 'Corporación BCT S.A',
    from: '2008',
    to: '2018',
    position: 'Ingeniero de Software Senior / Arquitecto',
    responsibilities: [
      'Proporcioné soporte técnico crítico para el sistema de banca en línea "Enl@ce", priorizando la fiabilidad y seguridad del servicio 24/7.',
      'Diseñé y mantuve sistemas de datos financieros de alto volumen, asegurando el estricto cumplimiento de los estándares de precisión de la industria bancaria.',
      'Lideré esfuerzos de integración continua y mejora de sistemas para plataformas de gestión interna.',
      'Audité y actualicé arquitecturas de bases de datos relacionales para garantizar la integridad de la información financiera sensible.',
      'Optimicé el rendimiento del sistema de banca en línea Enl@ce, logrando una reducción del 60% en los tiempos de carga mediante la implementación estratégica de jQuery y la optimización de procesos de renderizado y manipulación de datos en el cliente.'
    ],
    techStack: [
      '✓ Sistema Principal: Banca en línea Enl@ce',
      '✓ Gestión de Bases de Datos: SQL Server',
      '✓ Frontend: HTML, JavaScript, jQuery, MVC.NET, iOS',
      '✓ Backend: VB.NET, C#, SQL Server, XML',
    ],
  },
  {
    company: 'Sysde Costa Rica',
    from: '2007',
    to: '2008',
    position: 'Desarrollador de Software',
    responsibilities: [
      'Ejecuté proyectos de limpieza y migración de datos a gran escala para clientes internacionales dentro del ecosistema Siebel CRM.',
      'Automatice la detección de errores en los registros de clientes para garantizar datos de alta calidad para campañas de marketing multinacionales.',
      'Gestioné y optimicé sistemas electrónicos de marketing, centrándome en la integridad robusta de los datos y la disponibilidad del sistema.',
    ],
    techStack: [
      '✓ Sistema Principal: Marketing CRM',
      '✓ Gestión de Bases de Datos: SQL Server',
      '✓ Frontend: HTML, JavaScript, jQuery, MVC.NET, ASP',
      '✓ Backend: VB.NET, C#',
    ],
  },
];

const education = [
  {
    institution: 'Universidad Estatal a Distancia (UNED)',
    from: '2026',
    to: 'Presente',
    degree: 'Diplomado en informática',
    category: 'Universidad',
    certificate: [
      {
        title: 'Diploma',
        value: 'En curso',
      },
    ],
    images: ['/assets/img/education/uned.png'],
  },
  {
    institution: 'INA Instituto Nacional de Aprendizaje Costa Rica',
    from: '2026',
    to: '2026',
    degree: 'Cursos de educación continua',
    category: 'Tecnología',
    certificate: [
      {
        title: 'Certificado de participación',
        value: 'Manipulación de datos con SQL',
      },
    ],
    images: ['/assets/img/education/ina.png'],
  },
  {
    institution: 'Udemy',
    from: '2025',
    to: '2025',
    degree: 'Cursos de educación continua',
    category: 'Tecnología',
    certificate: [
      {
        title: 'Certificado',
        value: 'Haskell Coding Basics',
      },
      {
        title: 'Certificado',
        value: 'Python Flask: Make Web Apps with Python',
      },
      {
        title: 'Certificado',
        value: 'Javscript Essentials for Node-RED',
      },
      {
        title: 'Certificado',
        value: 'Mastering C++ Programming for beginners',
      },
      {
        title: 'Certificado',
        value: 'Aprende Lenguaje C de CERO a EXPERTO',
      },
    ],
    images: ['/assets/img/education/udemy.png'],
  },
  {
    institution: 'Cisco Netacad',
    from: '2025',
    to: '2025',
    degree: 'Cursos de educación continua',
    category: 'Tecnología',
    certificate: [
      {
        title: 'Certificado',
        value: 'English for IT 1',
      },
      {
        title: 'Certificado',
        value: 'Introduction to data cience',
      },
    ],
    images: ['/assets/img/education/cisco_netacad.png'],
  },
  {
    institution: 'Centro Cultural de Idiomas',
    from: '2017',
    to: '2019',
    degree: 'Ingles conversasional, Level 12 ( level: 60% )',
    category: 'Languages',
    certificate: [
      {
        title: 'TOEIC',
        value: 'B1',
      },
    ],
    images: ['/assets/img/certificates/centro-cultural-idiomas.jpg'],
  },
  {
    institution: 'New horizons',
    from: '2015',
    to: '2015',
    degree: 'Certificación en HTML5, CSS, Javascript and MVC.net',
    category: 'Technical',
    certificate: [
      {
        title: 'Certificado',
        value: 'Conclusión',
      },
    ],
    images: ['/assets/img/education/new-horizons.png'],
  },
  {
    institution: 'Cenfotec',
    from: '2009',
    to: '2009',
    degree: 'Certificación en XML with Visual Studio',
    category: 'Technical',
    certificate: [
      {
        title: 'Certificado',
        value: 'Conclusión',
      },
    ],
    images: ['/assets/img/education/cenfotec.png'],
  },
  {
    institution: 'Universidad Nacional de Costa Rica',
    from: '2004',
    to: '2008',
    degree: 'Ingenieria de sistemas',
    category: 'Universidad',
    certificate: [
      {
        title: 'Bachillerato',
        value: '',
      },
    ],
    images: ['/assets/img/education/una.png'],
  },
  {
    institution: 'Liceo El Roble',
    from: '1997',
    to: '2002',
    degree: 'Estudios secundarios',
    category: 'Colegio',
    certificate: [
      {
        title: 'Certificado',
        value: 'Conclusión',
      },
    ],
    images: ['/assets/img/education/liceo-roble.jpeg'],
    display: false
  },
  {
    institution: 'Liceo El Roble',
    from: '1990',
    to: '1996',
    degree: 'Escuela primaria',
    category: 'Escuela',
    certificate: [
      {
        title: 'Certificado',
        value: 'Conclusión',
      },
    ],
    images: ['/assets/img/education/liceo-roble.jpeg'],
    display: false
  },
];


const DataEs = {
  defaultThemeColor,
  about,
  skills,
  projects,
  experiences,
  education,
  getExperienceDuration,
};

export default DataEs;
